import React, { useState } from "react";
import { API_BASE_URL } from "../../config";

// const API_BASE_URL = "https://screening.fanisko.com";
// const API_BASE_URL = "http://127.0.0.1:8000";

const initialSections = [
  { section: "Experience", weightage: 20 },
  { section: "Projects", weightage: 30 },
  { section: "Education", weightage: 25 },
  { section: "Skills", weightage: 25 },
];


// Existing onboarding fields without type (type only added later on new fields)
const defaultOnboardingFields = [
  { key: "linkedin", label: "Please provide your LinkedIn profile link" },
  { key: "has_cover_letter", label: "Do you have a Cover Letter to upload?" },
  { key: "cover_letter", label: "Would you like to upload a cover letter to support your application?", type: "coverLetter" }, // preserve type for cover letter UI
  { key: "work_mode", label: "What is your preferred work mode?" },
  { key: "preferred_city", label: "Which city would you prefer to work in?" },
  { key: "current_location", label: "What is your current location?" },
  { key: "notice_period", label: "What is your notice period with your current employer (in months)?" },
  { key: "current_salary", label: "What is your current total annual salary (in INR)? (If you are a fresher, type NA)" },
  { key: "expected_salary", label: "What is your expected total annual salary (in INR)?" },
  { key: "job_change_reason", label: "What is the reason for your job change?" },
  { key: "career_gap_reason", label: "Do you have any gap in your career? If yes, please state the reason." },
  { key: "interview_date", label: "Please select your preferred interview date", type: "date" },
  { key: "interview_time_slots", label: "Please select your preferred interview time slot." },
  { key: "legally_authorized", label: "Are you legally authorized to work in ", type: "legalAuthorization" },
  { key: "open_to_relocate", label: "Are you open to relocating for this position?" },
  { key: "found_us", label: "Where did you find us?" },
];

export default function PostJobFormLayout() {
  const [step, setStep] = useState(1);
  const [authorizedCountries, setAuthorizedCountries] = useState("India");// default or empty
  const [loadingJD, setLoadingJD] = useState(false);
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const [dateRangeField, setDateRangeField] = useState(null);
  const [timeSlotOverlayOpen, setTimeSlotOverlayOpen] = useState(false);
  const [pendingTimeSlotSelections, setPendingTimeSlotSelections] = useState([]);

  // Initialize onboardingData as array with default toggles on for keys in this set
  const defaultTogglesOn = new Set([
    "linkedin",
    "cover_letter",
    "has_cover_letter",
    "work_mode",
    "preferred_city",
    "notice_period",
    "job_change_reason",
    "career_gap_reason",
    "current_location",
    "interview_date",
    "interview_time_slots",
    "current_salary",
    "expected_salary",
    "legally_authorized",
  ]);

  const initOnboardingData = defaultOnboardingFields.map((field) => ({
    ...field,
    value:
      field.key === "interview_date"
        ? { from: "", to: "" }
        : field.key === "interview_time_slots"
          ? []
          : "",
    enabled: field.key === "legally_authorized" ? true : defaultTogglesOn.has(field.key),
  }));


  const [onboardingData, setOnboardingData] = useState(initOnboardingData);

  // Other form states (same as before)
  const [sections, setSections] = useState(initialSections);
  const [form, setForm] = useState({
    jobTitle: "",
    jobDescription: "",
    educationalQualifications: "",
    experienceRequired: "",
    desiredSkills: "",
    requiredCertifications: "",
    goodToHave: "",
    employmentType: "",
    workMode: [],
    jobLocation: [],
    joiningRequirement: "",
    salaryPackage: "",
    show: "on",
  });

  const [workModes, setWorkModes] = useState([]);
  const [cityInput, setCityInput] = useState("");
  const [jobLocations, setJobLocations] = useState([]);

  const [newFieldLabel, setNewFieldLabel] = useState("");
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const addNewField = () => {
    const label = newFieldLabel.trim();
    if (!label) return;

    // Create unique key
    const keyBase = label.toLowerCase().replace(/[^\w]+/g, "_");
    const key = `${keyBase}_${Date.now()}`;

    // Prevent duplicates by label
    if (onboardingData.some((f) => f.label.toLowerCase() === label.toLowerCase())) {
      alert("Field with this label already exists.");
      return;
    }

    // New field with type "text" explicitly set
    const newField = { key, label, type: "text", value: "", enabled: true };

    setOnboardingData((prev) => [...prev, newField]);
    setNewFieldLabel("");
  };

  const handleToggle = (key) => {
    setOnboardingData((prev) => {
      // For interview_date and interview_time_slots: always match their enabled status
      let nextState = !prev.find(f => f.key === key).enabled;
      // Both toggles must match
      if (key === "interview_date" || key === "interview_time_slots") {
        return prev.map((f) => {
          if (f.key === "interview_date" || f.key === "interview_time_slots") {
            return { ...f, enabled: nextState };
          }
          // Cover letter pairing
          if (
            (key === "has_cover_letter" && f.key === "cover_letter") ||
            (key === "cover_letter" && f.key === "has_cover_letter")
          ) {
            return { ...f, enabled: nextState };
          }
          if (f.key === key) return { ...f, enabled: nextState };
          return f;
        });
      }
      // Normal toggle behavior for others
      return prev.map((f) => {
        if (f.key === key) return { ...f, enabled: nextState };
        if (
          (key === "has_cover_letter" && f.key === "cover_letter") ||
          (key === "cover_letter" && f.key === "has_cover_letter")
        ) {
          return { ...f, enabled: nextState };
        }
        return f;
      });
    });
  };


  const handleOnboardingChange = (key, value) => {
    setOnboardingData((prev) =>
      prev.map((f) =>
        f.key === key ? { ...f, value } : f
      )
    );
  };

  // Handlers for step 1 work modes and job locations
  const availableWorkModes = ["Remote", "Onsite", "Hybrid"];
  const handleAddWorkMode = (mode) => {
    if (!workModes.includes(mode)) {
      const updatedModes = [...workModes, mode];
      setWorkModes(updatedModes);
      setForm((prev) => ({ ...prev, workMode: updatedModes }));
    }
  };

  const handleRemoveWorkMode = (mode) => {
    const updatedModes = workModes.filter((m) => m !== mode);
    setWorkModes(updatedModes);
    setForm((prev) => ({ ...prev, workMode: updatedModes }));
  };

  const handleAddCity = () => {
    const city = cityInput.trim();
    if (city && !jobLocations.includes(city)) {
      const updatedLocations = [...jobLocations, city];
      setJobLocations(updatedLocations);
      setForm((prev) => ({ ...prev, jobLocation: updatedLocations.join(",") }));
    }
    setCityInput("");
  };

  const handleRemoveCity = (cityToRemove) => {
    const updatedLocations = jobLocations.filter((c) => c !== cityToRemove);
    setJobLocations(updatedLocations);
    setForm((prev) => ({ ...prev, jobLocation: updatedLocations.join(",") }));
  };

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  function parsePointers(str) {
    return str
      .split(/\n|\r/g)
      .map((s) => s.replace(/^\s*[-•*]\s?/, "").trim())
      .filter(Boolean);
  }

  function handleSectionChange(idx, field, value) {
    setSections((sections) =>
      sections.map((s, i) =>
        i === idx ? { ...s, [field]: field === "weightage" ? value : value } : s
      )
    );
  }

  function handleAddSection() {
    setSections([...sections, { section: "", weightage: "" }]);
  }

  function handleRemoveSection(idx) {
    setSections(sections.filter((_, i) => i !== idx));
  }

  function generateTimeSlots(start = 8, end = 21) {
    const slots = [];
    for (let hour = start; hour < end; hour++) {
      slots.push(
        `${hour.toString().padStart(2, '0')}:00 - ${hour.toString().padStart(2, '0')}:30`,
        `${hour.toString().padStart(2, '0')}:30 - ${(hour + 1).toString().padStart(2, '0')}:00`
      );
    }
    return slots;
  }
  const TIME_SLOT_OPTIONS = generateTimeSlots();


  // Validate step 1 required fields
  const validateStep1 = () => {
    console.log("jobTitle:", form.jobTitle);
    console.log("jobDescription:", form.jobDescription);
    console.log("educationalQualifications:", form.educationalQualifications);
    console.log("experienceRequired:", form.experienceRequired);
    console.log("desiredSkills:", form.desiredSkills);
    console.log("employmentType:", form.employmentType);
    console.log("workModes:", workModes);
    console.log("jobLocations:", jobLocations);
    console.log("salaryPackage:", form.salaryPackage);

    return (
      form.jobTitle.trim() &&
      form.jobDescription.trim() &&
      form.educationalQualifications.trim() &&
      form.experienceRequired.trim() &&
      form.desiredSkills.trim() &&
      form.employmentType.trim() &&
      workModes.length > 0 &&
      jobLocations.length > 0
      // form.salaryPackage.trim()
    );
  };

  // Validate onboarding fields if toggled on (optional, you can add more validation if desired)
  const validateStep2 = () => {
    // Example: Check if legally_authorized is required and filled
    if (onboardingData.toggles.legally_authorized) {
      return onboardingData.values.legally_authorized.trim() !== "";
    }
    return true;
  };

  async function generateJobDescription() {
    if (!form.jobTitle.trim()) {
      alert("Please enter a job title first");
      return;
    }
    setLoadingJD(true);
    setError("");
    try {
      const response = await fetch(`${API_BASE_URL}/api/generate-job-description`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ job_title: form.jobTitle.trim() }),
      });
      if (!response.ok) throw new Error("Failed to generate job description");
      const data = await response.json();

      // Update form fields with generated data from response
      setForm((prev) => ({
        ...prev,
        jobDescription: Array.isArray(data["Job Description"]) ? data["Job Description"].join("\n") : data["Job Description"],
        educationalQualifications: Array.isArray(data["Educational Qualifications"]) ? data["Educational Qualifications"].join("\n") : data["Educational Qualifications"],
        experienceRequired: data["Experience Required"] || "",
        desiredSkills: Array.isArray(data["Desired Skills"]) ? data["Desired Skills"].join("\n") : data["Desired Skills"],
        goodToHave: Array.isArray(data["Good to Have"]) ? data["Good to Have"].join("\n") : data["Good to Have"],
      }));

      setSuccess("Job description generated successfully!");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingJD(false);
    }
  }



  async function handlePostJob() {
    setPosting(true);
    setError("");
    setSuccess("");

    if (!validateStep1()) {
      setError("Please fill all required fields in Step 1 marked *");
      setPosting(false);
      return;
    }
    const hasCoverLetter = onboardingData.find(f => f.key === "has_cover_letter")?.enabled;
    const coverLetterOn = onboardingData.find(f => f.key === "cover_letter")?.enabled;

    if (hasCoverLetter !== coverLetterOn) {
      setError("Cover Letter options must BOTH be ON or BOTH be OFF.");
      setPosting(false);
      return;
    }
    const legallyAuthField = onboardingData.find(f => f.key === "legally_authorized");
    if (legallyAuthField?.enabled && !authorizedCountries.trim()) {
      setError("Please specify the country for legal authorization.");
      setPosting(false);
      return;
    }
    // if (!validateStep2()) {
    //   setError("Please fill all required onboarding fields marked *");
    //   setPosting(false);
    //   return;
    // }

    const payload = {
      jobrole: form.jobTitle,
      jobDescription: parsePointers(form.jobDescription),
      educationalQualifications: parsePointers(form.educationalQualifications),
      experienceRequired: form.experienceRequired,
      desiredSkills: parsePointers(form.desiredSkills),
      requiredCertifications: parsePointers(form.requiredCertifications),
      goodToHave: parsePointers(form.goodToHave),
      employmentType: form.employmentType,
      workMode: workModes,
      location: jobLocations,
      joiningRequirement: form.joiningRequirement,
      salaryPackage: form.salaryPackage,
      sectionWeightage: sections.reduce((acc, s) => {
        if (s.section) acc[s.section] = Number(String(s.weightage).trim() || "0");
        return acc;
      }, {}),
      show: form.show || "on",
      onboarding: onboardingData.reduce((acc, field) => {
        if (field.enabled) {
          if (field.key === "legally_authorized") {
            acc[field.key] = {
              label: field.label + authorizedCountries + "?",
              value: authorizedCountries,
              type: field.type || "text",
            };
          } else {
            acc[field.key] = {
              label: field.label,
              value: field.value || "",
              ...(field.type ? { type: field.type } : {}),
            };
          }
        }
        return acc;
      }, {}),
    };

    try {
      const res = await fetch(`${API_BASE_URL}/api/jobs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error("Failed to post job");
      const data = await res.json();
      setSuccess(`Job added successfully! Job ID: ${data.jobid}. JD has been structured and stored.`);
      // Reset everything
      setForm({
        jobTitle: "",
        jobDescription: "",
        educationalQualifications: "",
        experienceRequired: "",
        desiredSkills: "",
        requiredCertifications: "",
        goodToHave: "",
        employmentType: "",
        workMode: [],
        jobLocation: [],
        joiningRequirement: "",
        salaryPackage: "",
        show: "on"
      });
      setWorkModes([]);
      setJobLocations([]);
      setCityInput("");
      setSections(initialSections);

      // Reset onboarding
      setOnboardingData(
        defaultOnboardingFields.map((field) => ({
          ...field,
          value: "",
          enabled: defaultTogglesOn.has(field.key),
        }))
      );

      setStep(1); // back to first step
    } catch (err) {
      setError(err.message);
    }

    setPosting(false);
  }

  // Styles (simplified, reuse yours)
  const inputStyle = {
    width: "100%",
    height: 40,
    fontSize: 15,
    borderRadius: 6,
    border: "1px solid #2782e2",
    padding: "0 12px",
    background: "#f7fbff"
  };
  const inputAreaStyle = {
    width: "100%",
    fontSize: 15,
    borderRadius: 6,
    border: "1px solid #2782e2",
    padding: "10px 12px",
    background: "#f7fbff"
  };
  const labelStyle = {
    fontWeight: 700,
    color: "#455e88",
    fontSize: 15,
    display: "block",
    marginBottom: 7
  };
  const helperStyle = {
    color: "#818fb1",
    fontSize: 13,
    marginBottom: 4
  };

  const Switchlegallyautho = ({ checked, onChange, disabled }) => (
    <label style={{
      display: "inline-block", position: "relative", width: 40, height: 22, opacity: disabled ? 0.6 : 1,
      pointerEvents: disabled ? "none" : "auto"  // ignore clicks if disabled
    }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        style={{ opacity: 0, width: 0, height: 0 }}
      />
      <span
        style={{
          position: "absolute",
          cursor: disabled ? "default" : "pointer",
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: checked ? "#14e30dc9" : "#f31e1ec4",
          borderRadius: 22,
          transition: ".4s"
        }}
      />
      <span
        style={{
          position: "absolute",
          height: 18,
          width: 18,
          left: checked ? 18 : 2,
          bottom: 2,
          backgroundColor: "white",
          borderRadius: "50%",
          transition: ".4s"
        }}
      />
    </label>
  );

  // Render toggles for onboarding fields
  const Switch = ({ checked, onChange }) => (
    <label style={{ display: "inline-block", position: "relative", width: 40, height: 22 }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        style={{ opacity: 0, width: 0, height: 0 }}
      />
      <span
        style={{
          position: "absolute",
          cursor: "pointer",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: checked ? "#14e30dc9" : "#f31e1ec4",
          borderRadius: 22,
          transition: ".4s"
        }}
      />
      <span
        style={{
          position: "absolute",
          height: 18,
          width: 18,
          left: checked ? 18 : 2,
          bottom: 2,
          backgroundColor: "white",
          borderRadius: "50%",
          transition: ".4s"
        }}
      />
    </label>
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        background: "#ecf6fe",
        minHeight: "100vh",
        padding: "40px 0",
        fontFamily: "Arial, sans-serif"
      }}
    >
      {step === 1 && (
        <>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "flex-start",
              gap: 40,
              width: "100%",
              maxWidth: 1300
            }}
          >
            {/* LEFT FORM */}
            <div
              style={{
                flex: 1,
                background: "#fff",
                borderRadius: 13,
                boxShadow: "0 2px 16px #b3ddff20",
                marginLeft: 40,
                padding: 40
              }}
            >
              <h2
                style={{
                  color: "#172d44",
                  fontWeight: 700,
                  fontSize: "2rem",
                  marginBottom: 18
                }}
              >
                Post a Job
              </h2>
              <div style={{ marginBottom: 22 }}>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 7 }}>
                  <label
                    style={{
                      fontWeight: 700,
                      color: "#455e88",
                      fontSize: 15,
                      marginRight: 16,
                    }}
                  >
                    Job Title <span style={{ color: "red" }}>*</span>
                  </label>
                  <button
                    onClick={generateJobDescription}
                    disabled={loadingJD}
                    style={{
                      padding: "10px 28px",
                      fontWeight: "bold",
                      fontSize: "16px",
                      background: loadingJD
                        ? "#b3b3b3"
                        : "linear-gradient(90deg, #3381e7ff 0%, #3ec6e7 100%)",
                      color: "white",
                      border: loadingJD ? "2px solid #b3b3b3" : "2px solid #3381e7ff",
                      borderRadius: "25px",
                      cursor: loadingJD ? "not-allowed" : "pointer",
                      boxShadow: loadingJD
                        ? "none"
                        : "0 4px 14px 0 rgba(51, 129, 231, 0.15), 0 1.5px 4px rgba(50,198,231,.12)",
                      whiteSpace: "nowrap",
                      marginLeft: "auto",
                      transition: "background 0.3s, box-shadow 0.3s, border 0.3s",
                    }}

                    title="Generate Job Description"
                  >
                    {loadingJD ? "Generating..." : "Generate"}
                  </button>
                </div>
                <input
                  type="text"
                  name="jobTitle"
                  value={form.jobTitle}
                  onChange={handleChange}
                  placeholder="Enter job title"
                  style={{
                    ...inputStyle,
                    width: "100%",
                  }}
                />
              </div>

              <div style={{ marginBottom: 22 }}>
                <label style={labelStyle}>
                  Job Description <label style={{ color: "red" }}>*</label>
                </label>
                <div style={helperStyle}>
                  Each line or bullet will be stored as a pointer.
                </div>
                <textarea
                  name="jobDescription"
                  value={form.jobDescription}
                  onChange={handleChange}
                  rows={5}
                  placeholder="Describe the job role and responsibilities"
                  style={inputAreaStyle}
                />
              </div>
              <div style={{ marginBottom: 22 }}>
                <label style={labelStyle}>
                  Educational Qualifications <label style={{ color: "red" }}>*</label>
                </label>
                <div style={helperStyle}>
                  Each line or bullet will be stored as a pointer.
                </div>
                <textarea
                  name="educationalQualifications"
                  value={form.educationalQualifications}
                  onChange={handleChange}
                  rows={2}
                  placeholder="Enter required qualifications"
                  style={inputAreaStyle}
                />
              </div>
              <div style={{ marginBottom: 22 }}>
                <label style={labelStyle}>
                  Experience Required <label style={{ color: "red" }}>*</label>
                </label>
                <input
                  type="text"
                  name="experienceRequired"
                  value={form.experienceRequired}
                  onChange={handleChange}
                  placeholder="Ex: 3+ years"
                  style={inputStyle}
                />
              </div>
              <div style={{ marginBottom: 22 }}>
                <label style={labelStyle}>
                  Desired Skills <label style={{ color: "red" }}>*</label>
                </label>
                <div style={helperStyle}>Each skill on a new line or with a bullet.</div>
                <textarea
                  name="desiredSkills"
                  value={form.desiredSkills}
                  onChange={handleChange}
                  rows={2}
                  placeholder="List desired skills"
                  style={inputAreaStyle}
                />
              </div>
              <div style={{ marginBottom: 22 }}>
                <label style={labelStyle}>Required Certifications</label>
                <div style={helperStyle}>Each on a new line or with a bullet.</div>
                <textarea
                  name="requiredCertifications"
                  value={form.requiredCertifications}
                  onChange={handleChange}
                  rows={2}
                  placeholder="List required certifications"
                  style={inputAreaStyle}
                />
              </div>
              <div style={{ marginBottom: 22 }}>
                <label style={labelStyle}>Good to Have</label>
                <div style={helperStyle}>Each on a new line or with a bullet.</div>
                <textarea
                  name="goodToHave"
                  value={form.goodToHave}
                  onChange={handleChange}
                  rows={2}
                  placeholder="Optional points"
                  style={inputAreaStyle}
                />
              </div>
              <div style={{ marginBottom: 22 }}>
                <label style={labelStyle}>
                  Employment Type <label style={{ color: "red" }}>*</label>
                </label>
                <select
                  name="employmentType"
                  value={form.employmentType || ""}
                  onChange={handleChange}
                  style={{ ...inputStyle, height: 40 }}
                >
                  <option value="">Select</option>
                  <option value="Contract">Contract</option>
                  <option value="Full-Time Employment">Full-Time Employment</option>
                  <option value="Part-Time Employment">Part-Time Employment</option>
                  <option value="Internship">Internship</option>
                </select>
              </div>
              <div style={{ marginBottom: 22 }}>
                <label style={labelStyle}>
                  Work Mode <label style={{ color: "red" }}>*</label>
                </label>
                <div
                  style={{
                    ...inputStyle,
                    minHeight: 40,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 6,
                    alignItems: "center"
                  }}
                >
                  {workModes.map((mode) => (
                    <div
                      key={mode}
                      style={{
                        background: "#d1f0e4",
                        padding: "4px 10px",
                        borderRadius: 14,
                        display: "flex",
                        alignItems: "center"
                      }}
                    >
                      <span style={{ marginRight: 8 }}>{mode}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveWorkMode(mode)}
                        style={{
                          border: "none",
                          background: "transparent",
                          cursor: "pointer",
                          fontWeight: "bold"
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {availableWorkModes
                    .filter((opt) => !workModes.includes(opt))
                    .map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        style={{
                          background: "#eef6fb",
                          color: "#2373f0",
                          border: "1px solid #2373f0",
                          borderRadius: 16,
                          padding: "2px 15px",
                          marginLeft: 8,
                          marginRight: 4,
                          cursor: "pointer",
                          fontWeight: "600",
                          display: "flex",
                          alignItems: "center"
                        }}
                        onClick={() => handleAddWorkMode(mode)}
                      >
                        <span style={{ marginRight: 5 }}>{mode}</span>
                        <span
                          style={{
                            fontWeight: "bold",
                            marginTop: -5,
                            fontSize: 20,
                            color: "#2373f0"
                          }}
                        >
                          ＋
                        </span>
                      </button>
                    ))}
                </div>
              </div>
              <div style={{ marginBottom: 22 }}>
                <label style={labelStyle}>
                  Job Location <label style={{ color: "red" }}>*</label>
                </label>
                <div
                  style={{
                    ...inputStyle,
                    minHeight: 40,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 6,
                    alignItems: "center"
                  }}
                >
                  {jobLocations.map((city) => (
                    <div
                      key={city}
                      style={{
                        background: "#cce4ff",
                        padding: "4px 10px",
                        borderRadius: 14,
                        display: "flex",
                        alignItems: "center"
                      }}
                    >
                      <span style={{ marginRight: 8 }}>{city}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveCity(city)}
                        style={{
                          border: "none",
                          background: "transparent",
                          cursor: "pointer",
                          fontWeight: "bold"
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <input
                    type="text"
                    value={cityInput}
                    onChange={(e) => setCityInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddCity();
                      }
                    }}
                    placeholder="Type city and press Enter"
                    style={{ border: "none", outline: "none", flex: "1 0 120px", minWidth: 120 }}
                  />
                </div>
              </div>
              <div style={{ marginBottom: 22 }}>
                <label style={labelStyle}>Joining Requirement</label>
                <input
                  type="text"
                  name="joiningRequirement"
                  value={form.joiningRequirement}
                  onChange={handleChange}
                  placeholder="Ex: Immediate, 2 months, etc."
                  style={inputStyle}
                />
              </div>
              <div style={{ marginBottom: 22 }}>
                <label style={labelStyle}>Salary Package</label>
                <input
                  type="text"
                  name="salaryPackage"
                  value={form.salaryPackage}
                  onChange={handleChange}
                  placeholder="Ex: 8-12 LPA"
                  style={inputStyle}
                />
              </div>
            </div>

            {/* RIGHT FORM */}
            <div
              style={{
                width: 400,
                background: "#fff",
                borderRadius: 13,
                boxShadow: "0 2px 16px #b3ddff20",
                padding: 40,
                marginTop: 280,
                marginRight: 40,
                height: "fit-content"
              }}
            >
              <h2
                style={{
                  color: "#172d44",
                  fontWeight: 700,
                  fontSize: "1.5rem",
                  marginBottom: 18
                }}
              >
                Section Weightage
              </h2>
              {sections.map((sec, idx) => (
                <div
                  key={idx}
                  style={{ display: "flex", alignItems: "center", marginBottom: 16 }}
                >
                  <input
                    type="text"
                    value={sec.section}
                    placeholder="Section"
                    onChange={(e) => handleSectionChange(idx, "section", e.target.value)}
                    style={{
                      flex: "2 1 0",
                      padding: "10px 10px",
                      borderRadius: 6,
                      border: "1px solid #2782e2",
                      fontSize: 15,
                      background: "#fff"
                    }}
                  />
                  <input
                    type="number"
                    value={sec.weightage}
                    min={0}
                    max={100}
                    onChange={(e) => handleSectionChange(idx, "weightage", e.target.value)}
                    style={{
                      width: 90,
                      marginLeft: 12,
                      padding: "10px 10px",
                      borderRadius: 6,
                      border: "1px solid #2782e2",
                      fontSize: 15,
                      background: "#f7fbff"
                    }}
                  />
                  {idx >= initialSections.length && (
                    <button
                      type="button"
                      title="Remove"
                      onClick={() => handleRemoveSection(idx)}
                      style={{
                        marginLeft: 8,
                        background: "none",
                        border: "none",
                        color: "#e03c32",
                        fontSize: 20,
                        cursor: "pointer",
                        padding: 0
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={handleAddSection}
                style={{
                  marginTop: 10,
                  padding: "5px 15px",
                  fontSize: 18,
                  borderRadius: 6,
                  border: "1.5px solid #2684ff",
                  background: "#f7fbff",
                  color: "#2684ff",
                  cursor: "pointer"
                }}
              >
                ＋ Add Section
              </button>
            </div>
          </div>

          {/* Next Button */}
          <div style={{ marginTop: 40, textAlign: "center" }}>
            {error && <div style={{ color: "#e03c32", marginBottom: 12 }}>{error}</div>}
            <button
              onClick={() => {
                if (validateStep1()) {
                  console.log("Step 1 valid, moving to step 2");
                  setError("");
                  setStep(2);
                } else {
                  console.log("Step 1 validation failed");
                  setError("Please fill all required fields marked *");
                }
              }}
              style={{
                width: 240,
                height: 50,
                fontSize: 18,
                fontWeight: 700,
                background: "#2684ff",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                cursor: "pointer"
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = "#1b6fe0";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = "#2684ff";
              }}
            >
              Next
            </button>
          </div>
        </>
      )}
      {step === 2 && (
        <div
          style={{
            background: "#fff",
            padding: 40,
            borderRadius: 14,
            boxShadow: "0 2px 16px #b3ddff33",
            width: 600,
            fontSize: 16,
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          <h2 style={{ color: "#172d44", marginBottom: 20 }}>Onboarding Questions</h2>
          {/* New field input + button */}
          <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
            <input
              type="text"
              placeholder="Add new field label"
              value={newFieldLabel}
              onChange={(e) => setNewFieldLabel(e.target.value)}
              style={{
                flex: 1,
                borderRadius: 6,
                border: "1px solid #2782e2",
                padding: "8px 12px",
                fontSize: 15,
              }}
            />
            <button
              onClick={addNewField}
              style={{
                padding: "8px 18px",
                borderRadius: 6,
                backgroundColor: "#2684ff",
                color: "white",
                border: "none",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              ＋ Add Field
            </button>
          </div>

          {/* Onboarding fields and toggles ONLY */}
          {dateRangeOpen && (
            <div
              style={{
                position: "fixed",
                top: 0, left: 0,
                width: "100vw", height: "100vh",
                background: "rgba(44,68,121,0.10)",
                display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999
              }}
              onClick={() => setDateRangeOpen(false)}
            >
              <div
                style={{
                  background: "#fff", borderRadius: 12, padding: 35, minWidth: 340,
                  display: "flex", flexDirection: "column", alignItems: "center", boxShadow: "0 4px 18px #4441"
                }}
                onClick={e => e.stopPropagation()}
              >
                <h4 style={{ marginBottom: 18 }}>Select Interview Date Range</h4>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span>From</span>
                  <input
                    type="date"
                    min={(() => {
                      const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
                      return tomorrow.toISOString().slice(0, 10);
                    })()}
                    value={(onboardingData.find(f => f.key === "interview_date")?.value?.from) || ""}
                    onChange={e => handleOnboardingChange(
                      "interview_date",
                      {
                        ...(onboardingData.find(f => f.key === "interview_date")?.value || { from: "", to: "" }),
                        from: e.target.value
                      }
                    )}
                  />
                  <span>to</span>
                  <input
                    type="date"
                    min={
                      (onboardingData.find(f => f.key === "interview_date")?.value?.from)
                      || (() => {
                        const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1); return tomorrow.toISOString().slice(0, 10);
                      })()
                    }
                    value={(onboardingData.find(f => f.key === "interview_date")?.value?.to) || ""}
                    onChange={e => handleOnboardingChange(
                      "interview_date",
                      {
                        ...(onboardingData.find(f => f.key === "interview_date")?.value || { from: "", to: "" }),
                        to: e.target.value
                      }
                    )}
                  />
                </div>
                <button
                  style={{
                    marginTop: 28,
                    padding: "8px 22px",
                    borderRadius: 6,
                    background: "#2684ff",
                    color: "#fff",
                    fontWeight: 700,
                    border: "none",
                    cursor: "pointer",
                    fontSize: 16,
                  }}
                  onClick={() => setDateRangeOpen(false)}
                >
                  Done
                </button>
              </div>
            </div>
          )}

          {timeSlotOverlayOpen && (
            <div
              style={{
                position: 'fixed',
                top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.22)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 2300
              }}
              onClick={() => setTimeSlotOverlayOpen(false)}
            >
              <div
                style={{
                  background: '#fff',
                  borderRadius: 15,
                  padding: 32,
                  minWidth: 550,
                  maxWidth: 700,
                  marginLeft: 190,
                  boxShadow: '0 8px 32px rgba(38,129,231,0.13)',
                  position: 'relative'
                }}
                onClick={e => e.stopPropagation()}
              >
                <button
                  aria-label="Close"
                  style={{
                    position: 'absolute',
                    right: 14, top: 10, fontSize: 28, border: 'none',
                    background: 'none', color: '#2373f0', cursor: 'pointer'
                  }}
                  onClick={() => setTimeSlotOverlayOpen(false)}
                >×</button>
                <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 20 }}>Select Time Slot(s)</div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)', // 3 columns
                  gap: 14,
                  justifyItems: 'center'
                }}>
                  {TIME_SLOT_OPTIONS.map(opt => (
                    <button
                      key={opt}
                      style={{
                        width: 160,
                        padding: '13px 0px',
                        borderRadius: 9,
                        border: pendingTimeSlotSelections.includes(opt) ? '2px solid #2373f0' : '1px solid #c8d6fa',
                        background: pendingTimeSlotSelections.includes(opt) ? '#2373f0' : '#f7fafe',
                        color: pendingTimeSlotSelections.includes(opt) ? '#fff' : '#2373f0',
                        fontWeight: 700,
                        cursor: 'pointer',
                        textAlign: 'center'
                      }}
                      onClick={() => {
                        setPendingTimeSlotSelections(prev =>
                          prev.includes(opt)
                            ? prev.filter(s => s !== opt)
                            : [...prev, opt]
                        );
                      }}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
                <button
                  style={{
                    marginTop: 28,
                    padding: "8px 22px",
                    borderRadius: 6,
                    background: "#03d37cf4",
                    color: "#fff",
                    fontWeight: 700,
                    border: "none",
                    cursor: "pointer",
                    fontSize: 16,
                    marginLeft: 280,
                  }}
                  onClick={() => {
                    setTimeSlotOverlayOpen(false);
                    // Save in onboarding data as array
                    handleOnboardingChange("interview_time_slots", pendingTimeSlotSelections);
                  }}
                >
                  Save Selection
                </button>
              </div>
            </div>
          )}
          {onboardingData.map(({ key, label, enabled, type, value }) => {


            if (type === "legalAuthorization") {
              return (
                <div key={key} style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "1px solid #ddd",
                  paddingBottom: 10,
                  userSelect: "none",
                  marginBottom: 18
                }}>
                  <label style={{ ...labelStyle, color: "#344", fontWeight: 600 }}>
                    {label}
                    <input
                      type="text"
                      value={authorizedCountries}
                      onChange={e => setAuthorizedCountries(e.target.value)}
                      placeholder="Country"
                      style={{ marginLeft: 6, border: "1px solid #000000ff", borderRadius: 4, padding: "4px 6px" }}
                    />?
                  </label>
                  <Switchlegallyautho checked={true} onChange={() => { }} disabled={true} />
                </div>
              );
            }
            if (key === "interview_date") {
              const tomorrow = new Date();
              tomorrow.setDate(tomorrow.getDate() + 1);
              const yyyy = tomorrow.getFullYear();
              const mm = String(tomorrow.getMonth() + 1).padStart(2, '0');
              const dd = String(tomorrow.getDate()).padStart(2, '0');
              const minDate = `${yyyy}-${mm}-${dd}`;
              const curr = (typeof value === "object" && value !== null)
                ? value
                : { from: "", to: "" };
              const display = (curr.from && curr.to)
                ? `${curr.from} to ${curr.to}`
                : "Select Date Range";
              return (
                <div key={key} style={{
                  display: "flex",
                  justifyContent: "space-between",   // main fix - ensures right alignment
                  alignItems: "center",
                  borderBottom: "1px solid #ddd",
                  paddingBottom: 10,
                  userSelect: "none",
                  marginBottom: 18,
                }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 15 }}>
                    <span style={{ color: "#344", fontWeight: 600 }}>{label}</span>
                    <button
                      type="button"
                      style={{
                        padding: "8px 16px",
                        borderRadius: 5,
                        background: "#eef6fb",
                        border: "1px solid #2373f0",
                        color: "#2373f0",
                        fontWeight: 600,
                        cursor: "pointer",
                        marginLeft: 50
                      }}
                      onClick={() => {
                        setDateRangeOpen(true);
                        setDateRangeField(key);
                      }}
                    >
                      {display}
                    </button>
                  </span>
                  <Switch checked={enabled} onChange={() => handleToggle(key)} />
                </div>
              );
            }


            // Interview time field with range UI
            if (key === "interview_time_slots") {
              // Store value as an array
              const currSlots = Array.isArray(value) ? value : [];
              return (
                <div key={key} style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "1px solid #ddd",
                  paddingBottom: 10,
                  userSelect: "none",
                  marginBottom: 18,
                }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 15 }}>
                    <span style={{ color: "#344", fontWeight: 600 }}>{label}</span>
                    <button
                      type="button"
                      style={{
                        padding: "8px 16px",
                        borderRadius: 5,
                        background: "#eef6fb",
                        border: "1px solid #2373f0",
                        color: "#2373f0",
                        fontWeight: 600,
                        cursor: "pointer",
                        marginLeft: 6
                      }}
                      onClick={() => {
                        setTimeSlotOverlayOpen(true);
                        setPendingTimeSlotSelections(currSlots);
                      }}
                    >
                      {currSlots.length ? `Time slots selected` : "Choose Time Slot(s)"}
                    </button>
                  </span>
                  <Switch checked={enabled} onChange={() => handleToggle(key)} />
                </div>
              );
            }



            return (
              <div
                key={key}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "1px solid #ddd",
                  paddingBottom: 10,
                  userSelect: "none",
                }}
              >
                <span style={{ color: "#344", fontWeight: 600 }}>{label}</span>
                <Switch checked={enabled} onChange={() => handleToggle(key)} />
              </div>
            );
          })}


          {/* Back and Post buttons */}
          <div style={{ marginTop: 30, display: "flex", justifyContent: "space-between" }}>
            <button
              type="button"
              onClick={() => setStep(1)}
              style={{
                padding: "10px 25px",
                fontSize: 16,
                borderRadius: 6,
                border: "1px solid #2684ff",
                background: "#f7fbff",
                color: "#2684ff",
                cursor: "pointer"
              }}
            >
              Back
            </button>
            <button
              type="button"
              onClick={handlePostJob}
              disabled={posting}
              style={{
                padding: "10px 25px",
                fontSize: 16,
                borderRadius: 6,
                border: "none",
                background: posting ? "#b6d4fa" : "#2684ff",
                color: "#fff",
                cursor: posting ? "not-allowed" : "pointer"
              }}
              onMouseOver={(e) => {
                if (!posting) e.currentTarget.style.background = "#1b6fe0";
              }}
              onMouseOut={(e) => {
                if (!posting) e.currentTarget.style.background = "#2684ff";
              }}
            >
              {posting ? "Posting..." : "Post"}
            </button>
          </div>

          {error && <div style={{ color: "#e03c32", marginTop: 10 }}>{error}</div>}
          {success && <div style={{ color: "#23a262", marginTop: 10 }}>{success}</div>}
        </div>
      )}
    </div>
  );
}
